<template>
  <a-menu
    :items="menuItems"
    size="small"
    mode="vertical"
    @click="handleClick"
  ></a-menu>
  <a-modal
    :title="$t('设置')"
    width="1000px"
    v-model:open="settingOpen"
  >
    <column-edit-view
      :columns="columns"
      :is_view="is_view"
      :view_key="view_key"
      :view_flag_name="view_flag_name"
      :component_type="component_type"
      :pivotConfig="localPivotConfig"
      :pivotChartsConfig="localPivotChartsConfig"
      :settingMode="settingMode"
      :settingDrawerWidth="settingDrawerWidth"
      :settingDrawerPlacement="settingDrawerPlacement"
      @change="$emit('columnsChange', $event)"
      @pivotConfigChange="onPivotConfigChange"
      @pivotChartsConfigChange="onPivotChartsConfigChange"
    ></column-edit-view>
    <template #footer>
      <a-button @click="settingOpen = false">
        {{ $t('关闭') }}
      </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import {
  CopyOutlined,
  ReloadOutlined,
  DownloadOutlined,
  SettingOutlined,
  SaveOutlined,
  AlignLeftOutlined,
} from '@ant-design/icons-vue';

import { copyValue, isNotEmpty } from 'liyu-pc-base/utils/function';
import { computed, h, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import columnEditView from './tableConfig/index.vue';
import {
  PivotConfig,
  PivotChartsConfig,
  SettingMode,
  SettingDrawerPlacement,
  ContextMenuProps,
  MenuItem,
} from './typing';
import { useTableProState } from './utils';
import { MenuProps } from 'ant-design-vue';

const props = withDefaults(defineProps<ContextMenuProps>(), {
  dataSource: () => [],
  columns: () => [],
  view_key: '',
  view_flag_name: '',
  is_view: false,
  selfEventBus: null,
  component_type: 'normal',
  pivotConfig: () => null,
  pivotChartsConfig: () => null,
});

const emit = defineEmits([
  'refresh',
  'export',
  'columnsChange',
  'pivotConfigChange',
  'pivotChartsConfigChange',
]);

const settingOpen = ref(false);
const { currentView, isViewChange, config } = useTableProState();

// 「设置」弹窗交互方式：modal（模态框，默认）或 drawer（抽屉）
const settingMode = computed<SettingMode>(() => config?.setting_mode || 'modal');
const settingDrawerWidth = computed<string | number>(() => config?.setting_drawer_width ?? '700px');
const settingDrawerPlacement = computed<SettingDrawerPlacement>(
  () => config?.setting_drawer_placement || 'left',
);

// ============ 数据透视表相关 ============
const localPivotConfig = ref<PivotConfig>(
  props.pivotConfig
    ? cloneDeep(props.pivotConfig)
    : { rows: [], columns: [], values: [], indicatorsAsCol: true, rowHierarchyType: 'grid' },
);

watch(
  () => props.pivotConfig,
  val => {
    if (val) {
      localPivotConfig.value = cloneDeep(val);
    }
  },
  { deep: true },
);

const onPivotConfigChange = (cfg: PivotConfig) => {
  emit('pivotConfigChange', cfg);
};

// ============ 数据透视图相关 ============
const localPivotChartsConfig = ref<PivotChartsConfig>(
  props.pivotChartsConfig
    ? cloneDeep(props.pivotChartsConfig)
    : { series: [], category: [], values: [], chartType: 'bar' },
);

watch(
  () => props.pivotChartsConfig,
  val => {
    if (val) {
      localPivotChartsConfig.value = cloneDeep(val);
    }
  },
  { deep: true },
);

const onPivotChartsConfigChange = (cfg: PivotChartsConfig) => {
  emit('pivotChartsConfigChange', cfg);
};
/**
 * 递归处理菜单项的显示与禁用状态：
 * - disabledKeys：命中的菜单项直接移除（disabledTableMenuItemKeys）
 * - showFunc：未配置时默认显示；配置且返回 false 时隐藏该项
 * - disabledFunc：未配置时默认可用；配置且返回 true 时禁用该项
 * - 子菜单全部被隐藏时，无 func 的纯容器菜单项一并隐藏
 */
const processMenuItems = (list: MenuItem[], disabledKeys: string[] = []): any[] => {
  const res: any[] = [];
  for (const item of list) {
    const newItem: any = { ...item };
    if (item.children?.length) {
      newItem.children = processMenuItems(item.children, disabledKeys);
      if (!newItem.children?.length) {
        delete newItem.children;
      }
    }
    if (disabledKeys.includes(item.key)) {
      continue;
    }
    if (item.showFunc && !item.showFunc(props)) {
      continue;
    }
    if (!newItem.children && item.children?.length && !item.func) {
      continue;
    }
    newItem.disabled = !!(item.disabledFunc && item.disabledFunc(props));
    res.push(newItem);
  }
  return res;
};
const menuItems = computed<any[]>(() => {
  let res: MenuItem[] = [
    {
      key: 'copy',
      icon: () => h(CopyOutlined),
      label: '复制',
      children: [
        {
          key: 'copy_cell',
          icon: () => h(CopyOutlined),
          label: '复制单元格',
          func: () => copyClick('cell'),
        },
        {
          key: 'copy_record',
          icon: () => h(CopyOutlined),
          label: '复制行',
          func: () => copyClick('record'),
        },
        {
          key: 'copy_column',
          icon: () => h(CopyOutlined),
          label: '复制列',
          func: () => copyClick('column'),
        },
      ],
    },
    {
      key: 'refresh',
      icon: () => h(ReloadOutlined),
      label: '刷新',
      func: () => emit('refresh'),
    },
    {
      key: 'export',
      icon: () => h(DownloadOutlined),
      label: '导出',
      func: () => emit('export'),
    },
    {
      key: 'table_config',
      icon: () => h(SettingOutlined),
      label: '设置',
      func: () => (settingOpen.value = true),
    },
  ];
  if (
    props.is_view &&
    isViewChange.value &&
    currentView.value &&
    currentView.value.id !== 'no-view'
  ) {
    res = [
      ...res,
      {
        key: 'view',
        icon: () => h(AlignLeftOutlined),
        label: '视图',
        children: [
          {
            key: 'save_view',
            icon: () => h(SaveOutlined),
            label: '保存视图',
          },
          {
            key: 'save_new_view',
            icon: () => h(SaveOutlined),
            label: '另存视图',
          },
          {
            key: 'reinit',
            icon: () => h(ReloadOutlined),
            label: '重置',
          },
        ],
      },
    ];
  }
  if (config?.extraTableMenuItems?.length) {
    res = [...res, ...config.extraTableMenuItems];
  }
  return processMenuItems(res, config?.disabledTableMenuItemKeys);
});

const formatCopyValue = (value: any) => {
  return typeof value === 'object' ? JSON.stringify(value) : value;
};

const copyClick = (type: 'cell' | 'column' | 'record') => {
  const { contextmenuPopup, dataSource, columns } = props;
  if (type === 'cell') {
    if (contextmenuPopup.column.key !== 'operation') {
      copyValue(formatCopyValue(contextmenuPopup.text));
    }
  } else if (type === 'column') {
    if (contextmenuPopup.column.key !== 'operation') {
      const { dataIndex } = contextmenuPopup.column;
      const value = dataSource.map((d: any) => formatCopyValue(d[dataIndex])).join('\r\n');
      copyValue(value);
    }
  } else if (type === 'record') {
    const record = contextmenuPopup.record;
    const value = columns
      .map((c: any) => (c.dataIndex ? formatCopyValue(record[c.dataIndex]) : ''))
      .filter(c => isNotEmpty(c))
      .join(' ');
    copyValue(value);
  }
};
const findMenuItemByKey = (list: MenuItem[], key: string) => {
  for (let item of list) {
    if (item.key === key) {
      return item;
    }
    if (item.children) {
      const child = findMenuItemByKey(item.children, key);
      if (child) {
        return child;
      }
    }
  }
};
const handleClick: MenuProps['onClick'] = (menuInfo: any) => {
  const menuItem = findMenuItemByKey(menuItems.value, menuInfo.key);
  if (menuItem.func) {
    menuItem.func(props);
  }
  props.contextmenuPopup.hidePopup();
};
</script>

<style lang="less" scoped>
.popup-divider {
  :deep(.ant-divider-horizontal) {
    margin-top: 1px;
    margin-bottom: 1px;
  }
}
</style>
